'use client';

import { Reference } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { SaveListInput } from '@fdc/__generated__/graphql';
import { LISTS_OP_TYPE } from '@fdc/constants/lists-constants';
import { SAVE_SHOPPING_LIST } from '@fdc/federation/nz/mutations/save-shopping-list';
import {
  SHOPPING_LIST_FREE_TEXT_FRAGMENT,
  SHOPPING_LIST_PRODUCT_FRAGMENT,
} from '@fdc/federation/nz/queries/shopping-lists-detail-query';
import { SHOPPING_LISTS } from '@fdc/federation/nz/queries/shopping-lists-query';

type ShoppingListsCacheData = {
  lists?: Array<{
    id: string;
    referenceId: string;
    name: string;
    productCount: number;
    freeTextCount: number;
  }>;
};

export const useSaveShoppingList = () => {
  const [saveShoppingList, { loading, error }] = useMutation(
    SAVE_SHOPPING_LIST,
    {
      context: { skipGlobalErrorSnackbar: true },
      update: (cache, { data }, { variables }) => {
        const saveList = data?.saveList;

        if (!saveList) {
          return;
        }

        const listId = cache.identify({
          __typename: 'ListPayload',
          id: saveList.id,
        });

        const existingShoppingLists =
          typeof cache.readQuery === 'function'
            ? cache.readQuery<ShoppingListsCacheData>({
                query: SHOPPING_LISTS,
              })
            : null;

        const nextShoppingLists = existingShoppingLists?.lists ?? [];
        const listAlreadyInCache = nextShoppingLists.some(
          (list) => list.id === saveList.id
        );

        const productCreateCount =
          variables.input.products?.filter(
            (product) => product.opType === LISTS_OP_TYPE.CREATE
          ).length ?? 0;
        const productDeleteCount =
          variables.input.products?.filter(
            (product) => product.opType === LISTS_OP_TYPE.DELETE
          ).length ?? 0;
        const freeTextCreateCount =
          variables.input.freeTexts?.filter(
            (freeText) => freeText.opType === LISTS_OP_TYPE.CREATE
          ).length ?? 0;
        const freeTextDeleteCount =
          variables.input.freeTexts?.filter(
            (freeText) => freeText.opType === LISTS_OP_TYPE.DELETE
          ).length ?? 0;

        if (!listAlreadyInCache) {
          cache.writeQuery({
            query: SHOPPING_LISTS,
            data: {
              lists: [
                {
                  __typename: 'ListsItemPayload',
                  id: saveList.id,
                  referenceId: saveList.referenceId,
                  name: saveList.name,
                  productCount: saveList.products?.length ?? 0,
                  freeTextCount: saveList.freeTexts?.length ?? 0,
                },
                ...nextShoppingLists,
              ],
            },
          });
        } else {
          cache.writeQuery({
            query: SHOPPING_LISTS,
            data: {
              lists: nextShoppingLists.map((list) =>
                list.id === saveList.id
                  ? {
                      ...list,
                      name: saveList.name,
                      referenceId: saveList.referenceId,
                      productCount: Math.max(
                        0,
                        (list.productCount ?? 0) +
                          productCreateCount -
                          productDeleteCount
                      ),
                      freeTextCount: Math.max(
                        0,
                        (list.freeTextCount ?? 0) +
                          freeTextCreateCount -
                          freeTextDeleteCount
                      ),
                    }
                  : list
              ),
            },
          });
        }

        // Shopping list
        if (saveList.freeTexts.length === 0 && saveList.products.length === 0) {
          cache.modify({
            id: listId,
            fields: {
              name: () => saveList.name,
            },
          });
        }

        // Free text
        if (saveList.freeTexts.length > 0 && saveList.action === 'UPDATED') {
          if (variables.input.freeTexts[0].opType === LISTS_OP_TYPE.CREATE) {
            cache.modify({
              id: listId,
              fields: {
                freeTexts: (existingRefs: Reference[] = [], { readField }) => {
                  const newRefs = saveList.freeTexts
                    .filter(
                      (freeText) =>
                        !existingRefs.some(
                          (ref) =>
                            readField('id', ref) === freeText.id ||
                            String(readField('text', ref))
                              .trim()
                              .toLowerCase() ===
                              freeText.text.trim().toLowerCase()
                        )
                    )
                    .map((freeText) =>
                      cache.writeFragment({
                        fragment: SHOPPING_LIST_FREE_TEXT_FRAGMENT,
                        data: {
                          __typename: 'ListFreeTextPayload',
                          id: freeText.id,
                          referenceId: freeText.referenceId,
                          text: freeText.text,
                          timestamp: freeText.timestamp,
                        },
                      })
                    );

                  return [...existingRefs, ...newRefs];
                },
                freeTextCount: (existingCount = 0) => existingCount + 1,
              },
            });
          }

          if (variables.input.freeTexts[0].opType === LISTS_OP_TYPE.DELETE) {
            const freeTextId = variables.input.freeTexts[0].id;
            cache.modify({
              id: listId,
              fields: {
                freeTexts: (existingRefs: Reference[] = [], { readField }) => {
                  const newRefs = existingRefs.filter(
                    (ref) => String(readField('id', ref)) !== String(freeTextId)
                  );

                  return newRefs;
                },
                freeTextCount: (existingCount = 0) => existingCount - 1,
              },
            });
            cache.evict({
              id: cache.identify({
                __typename: 'ListFreeTextPayload',
                id: String(freeTextId),
              }),
            });
            cache.gc();
          }
        }

        // Products
        if (saveList.products.length > 0 && saveList.action === 'UPDATED') {
          if (variables.input.products[0].opType === LISTS_OP_TYPE.CREATE) {
            cache.modify({
              id: listId,
              fields: {
                products: (existingRefs: Reference[] = [], { readField }) => {
                  const newRefs = saveList.products
                    .filter(
                      (product) =>
                        !existingRefs.some(
                          (ref) =>
                            readField('id', ref) === product.id ||
                            readField('articleNumber', ref) ===
                              product.articleNumber
                        )
                    )
                    .map((product) =>
                      cache.writeFragment({
                        fragment: SHOPPING_LIST_PRODUCT_FRAGMENT,
                        data: {
                          __typename: 'ListProductPayload',
                          id: product.id,
                          referenceId: product.referenceId,
                          articleNumber: product.articleNumber,
                          variantKey: product.variantKey,
                          quantity: product.quantity,
                          timestamp: product.timestamp,
                        },
                      })
                    );

                  return [...existingRefs, ...newRefs];
                },
                productCount: (existingCount = 0) => existingCount + 1,
              },
            });
          }

          if (variables.input.products[0].opType === LISTS_OP_TYPE.UPDATE) {
            const productId = cache.identify({
              __typename: 'ListProductPayload',
              id: saveList.products[0].id,
            });

            cache.modify({
              id: productId,
              fields: {
                variantKey: () => saveList.products[0].variantKey,
                quantity: () => saveList.products[0].quantity,
              },
            });
          }

          if (variables.input.products[0].opType === LISTS_OP_TYPE.DELETE) {
            const productId = variables.input.products[0].id;
            cache.modify({
              id: listId,
              fields: {
                products: (existingRefs: Reference[] = [], { readField }) => {
                  const newRefs = existingRefs.filter(
                    (ref) => String(readField('id', ref)) !== String(productId)
                  );

                  return newRefs;
                },
                productCount: (existingCount = 0) => existingCount - 1,
              },
            });
            cache.evict({
              id: cache.identify({
                __typename: 'ListProductPayload',
                id: String(productId),
              }),
            });
            cache.gc();
          }
        }
      },
    }
  );

  const handleSaveShoppingList = (input: SaveListInput) => {
    return saveShoppingList({
      variables: {
        input,
      },
    });
  };

  return {
    handleSaveShoppingList,
    loading,
    error,
  };
};
